import { all, call, put, takeEvery } from '@redux-saga/core/effects';
import { notification } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import {
    parseWarehouseLoanTransaction,
} from '~Api/Warehouse/parsers';
import { IFetchResponse } from '~utilities/fetch';
import { renderNotificationLoadingIcon } from '~utilities/utils';
import {
    ILoanPaymentAbaGetAction,
    ILoanPaymentAbaHistoriesListAction,
    ILoanPaymentAbaWarehouseLoanTransactionsListAction,
    ILoanPaymentAbasAddAction,
    ILoanPaymentAbasListAction,
    ILoanPaymentAbasUnallocatedWarehouseLoanTransactionsListAction,
    loanPaymentAbaHistoriesSetAction,
    loanPaymentAbaSetAction,
    loanPaymentAbaWarehouseLoanTransactionsSetAction,
    loanPaymentAbasSetAction,
    loanPaymentAbasUnallocatedWarehouseLoanTransactionsClearAction,
    loanPaymentAbasUnallocatedWarehouseLoanTransactionsSetAction,
} from './actions';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import ILoanPaymentAba from '~Api/LoanPaymentAba/ILoanPaymentAba';
import ILoanPaymentAbaHistory from '~Api/LoanPaymentAba/ILoanPaymentAbaHistory';
import LoanPaymentAbasActionsEnum from './ActionsEnum';
import {
    loanPaymentAbaGetRequest,
    loanPaymentAbaHistoriesListRequest,
    loanPaymentAbaWarehouseLoanTransactionsListRequest,
    loanPaymentAbasAddRequest,
    loanPaymentAbasListRequest,
    loanPaymentAbasUnallocatedWarehouseLoanTransactionsListRequest,
} from '~Api/LoanPaymentAba/requests';
import {
    parseLoanPaymentAba,
    parseLoanPaymentAbaHistory,
} from '~Api/LoanPaymentAba/parsers';

function* loanPaymentAbaGet(action: ILoanPaymentAbaGetAction): Iterator<unknown> {
    const loanPaymentAbaResponse: IFetchResponse = yield call(loanPaymentAbaGetRequest, action.loanPaymentAbaUuid);
    const loanPaymentAba: ILoanPaymentAba = parseLoanPaymentAba(loanPaymentAbaResponse.body);
    yield put(loanPaymentAbaSetAction(loanPaymentAba));
}

function* loanPaymentAbasList(action: ILoanPaymentAbasListAction): Iterator<unknown> {
    const loanPaymentAbasResponse: IFetchResponse = yield call(loanPaymentAbasListRequest);
    const loanPaymentAbas: ILoanPaymentAba[] = yield Promise.all(loanPaymentAbasResponse.body.map(parseLoanPaymentAba));
    yield put(loanPaymentAbasSetAction(loanPaymentAbas));
}

function* loanPaymentAbaHistoriesList(action: ILoanPaymentAbaHistoriesListAction): Iterator<unknown> {
    const loanPaymentAbaHistoriesResponse: IFetchResponse = yield call(loanPaymentAbaHistoriesListRequest, action.loanPaymentAbaUuid);
    const loanPaymentAbaHistories: ILoanPaymentAbaHistory[] = yield Promise.all(loanPaymentAbaHistoriesResponse.body.map(parseLoanPaymentAbaHistory));
    yield put(loanPaymentAbaHistoriesSetAction(action.loanPaymentAbaUuid, loanPaymentAbaHistories));
}

function* loanPaymentAbaWarehouseLoanTransactionsList(action: ILoanPaymentAbaWarehouseLoanTransactionsListAction): Iterator<unknown> {
    const loanPaymentAbaWarehouseLoanTransactionsResponse: IFetchResponse = yield call(loanPaymentAbaWarehouseLoanTransactionsListRequest, action.loanPaymentAbaUuid);
    const loanPaymentAbaWarehouseLoanTransactions: IWarehouseLoanTransaction[] = yield Promise.all(loanPaymentAbaWarehouseLoanTransactionsResponse.body.map(parseWarehouseLoanTransaction));
    yield put(loanPaymentAbaWarehouseLoanTransactionsSetAction(action.loanPaymentAbaUuid, loanPaymentAbaWarehouseLoanTransactions));
}

function* loanPaymentAbasAdd(action: ILoanPaymentAbasAddAction): Iterator<unknown> {
    const key: string = `loanPaymentAbasAdd ${dayjs().format()}`;
    const message: string = 'Add Loan Payment ABA';

    notification.open({
        description: 'Adding loan payment ABA...',
        duration: 0,
        icon: renderNotificationLoadingIcon(),
        key,
        message,
    });

    const loanPaymentAbasAddResponse: IFetchResponse = yield call(loanPaymentAbasAddRequest, action.warehouseLoanTransactionUuids);
    if (loanPaymentAbasAddResponse.status === 422) {
        notification.error({
            description: `There was a problem adding the loan payment ABA: ${_.values(loanPaymentAbasAddResponse.body)[0]}.`,
            duration: 0,
            key,
            message,
        });
    } else {
        const loanPaymentAba: ILoanPaymentAba = parseLoanPaymentAba(loanPaymentAbasAddResponse.body);
        yield put(loanPaymentAbaSetAction(loanPaymentAba));

        notification.success({
            description: 'The loan payment ABA has been added.',
            duration: 4.5,
            key,
            message,
        });

        yield put(loanPaymentAbasUnallocatedWarehouseLoanTransactionsClearAction());
    }
}

function* LoanPaymentAbasUnallocatedWarehouseLoanTransactionsList(action: ILoanPaymentAbasUnallocatedWarehouseLoanTransactionsListAction): Iterator<unknown> {
    const unallocatedTransactionsResponse: IFetchResponse = yield call(loanPaymentAbasUnallocatedWarehouseLoanTransactionsListRequest);
    const unallocatedTransactions: IWarehouseLoanTransaction[] = yield Promise.all(unallocatedTransactionsResponse.body.map(parseWarehouseLoanTransaction));

    yield put(loanPaymentAbasUnallocatedWarehouseLoanTransactionsSetAction(unallocatedTransactions));
}

export function* LoanPaymentAbasSagas(): Iterator<unknown> {
    yield all([
        takeEvery(LoanPaymentAbasActionsEnum.LoanPaymentAbaGet, loanPaymentAbaGet),
        takeEvery(LoanPaymentAbasActionsEnum.LoanPaymentAbaHistoriesList, loanPaymentAbaHistoriesList),
        takeEvery(LoanPaymentAbasActionsEnum.LoanPaymentAbaWarehouseLoanTransactionsList, loanPaymentAbaWarehouseLoanTransactionsList),
        takeEvery(LoanPaymentAbasActionsEnum.LoanPaymentAbasAdd, loanPaymentAbasAdd),
        takeEvery(LoanPaymentAbasActionsEnum.LoanPaymentAbasList, loanPaymentAbasList),
        takeEvery(LoanPaymentAbasActionsEnum.LoanPaymentAbasUnallocatedWarehouseLoanTransactionsList, LoanPaymentAbasUnallocatedWarehouseLoanTransactionsList),
    ]);
}
